import { pblLanguages, pblLawFirmPositions, pblProfessionalQualifications, pblServiceAreas } from '@/data/pbl';
import type { PblServiceArea } from '@/types/pbl';

export type PblLanguageValue = typeof pblLanguages[number]['value'];
export type PblLawFirmPositionValue = typeof pblLawFirmPositions[number]['value'];
export type PblProfessionalQualificationValue = typeof pblProfessionalQualifications[number]['value'];

type ChoiceOption<Value extends string> = {
  defaultLabel: string;
  value: Value;
};

const languageLabelZhMap: Record<PblLanguageValue, string> = {
  english: '英語',
  cantonese: '粵語',
  mandarin: '普通話',
  other: '其他',
};

const qualificationLabelZhMap: Record<PblProfessionalQualificationValue, string> = {
  qualified_prc_lawyer: '中國內地合資格律師',
  qualified_gba_lawyer: '大灣區合資格律師',
  qualified_lawyer_overseas_jurisdictions: '海外司法管轄區合資格律師',
  other: '其他',
  not_applicable: '不適用',
};

const positionLabelZhMap: Record<PblLawFirmPositionValue, string> = {
  partner: '合夥人',
  consultant: '顧問',
  associate: '律師',
  other: '其他',
};

const serviceAreaLabelZhMap: Record<PblServiceArea, string> = {
  estate_planning: '遺產規劃（遺囑/持久授權書/預設醫療指示）',
  family_matters: '家庭事務（離婚/贍養費/撫養權/繼承）',
  employment_disputes: '雇傭糾紛',
  building_management: '樓宇管理（滲水/物業管理）',
  tenancy_and_properties: '租賃及物業相關事務',
  criminal_matters: '刑事事務',
};

const languageAliases: Record<PblLanguageValue, string[]> = {
  english: ['English', '英文', '英语', '英語'],
  cantonese: ['Cantonese', '廣東話', '广东话', '粵語', '粤语'],
  mandarin: ['Mandarin', 'Putonghua', '普通話', '普通话'],
  other: ['Other', '其他'],
};

const qualificationAliases: Record<PblProfessionalQualificationValue, string[]> = {
  qualified_prc_lawyer: ['Qualified PRC Lawyer', '中國內地合資格律師', '中国内地合资格律师'],
  qualified_gba_lawyer: ['Qualified GBA Lawyer', '大灣區合資格律師', '大湾区合资格律师'],
  qualified_lawyer_overseas_jurisdictions: [
    'Qualified lawyer in overseas jurisdictions',
    '海外司法管轄區合資格律師',
    '海外司法管轄區律師資格',
    '海外司法管辖区合资格律师',
    '海外司法管辖区律师资格',
  ],
  not_applicable: ['Not applicable', 'N/A', '不適用', '不适用'],
  other: ['Other', '其他'],
};

const positionAliases: Record<PblLawFirmPositionValue, string[]> = {
  partner: ['Partner', '合伙人', '合夥人'],
  consultant: ['Consultant', '顧問', '顾问'],
  associate: ['Associate', 'Lawyer', 'Solicitor', '律師', '律师'],
  other: ['Other', '其他'],
};

const serviceAreaAliases: Record<PblServiceArea, string[]> = {
  estate_planning: ['Estate Planning', 'Estate planning', '遺產規劃', '遗产规划'],
  family_matters: ['Family Matters', 'Family matters', '家庭事務', '家庭事务'],
  employment_disputes: [
    'Employment Disputes',
    'Employment disputes',
    '僱傭糾紛',
    '雇傭糾紛',
    '雇佣纠纷',
    '勞資糾紛',
    '劳资纠纷',
  ],
  building_management: ['Building Management', 'Building management', '樓宇管理', '楼宇管理'],
  tenancy_and_properties: [
    'Tenancy and properties related Matters',
    'Tenancy and properties related matters',
    '租賃及物業相關事務',
    '租赁及物业相关事务',
  ],
  criminal_matters: ['Criminal Matters', 'Criminal matters', '刑事事務', '刑事事务'],
};

const normalizeLookupKey = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const createChoiceLookup = <Value extends string>(
  options: ReadonlyArray<ChoiceOption<Value>>,
  localizedLabels: Record<Value, string>,
  aliases: Record<Value, string[]>
) => {
  const lookup = new Map<string, Value>();

  options.forEach((option) => {
    [option.value, option.defaultLabel, localizedLabels[option.value], ...aliases[option.value]].forEach((label) => {
      const normalized = normalizeLookupKey(label);
      if (normalized) lookup.set(normalized, option.value);
    });
  });

  return lookup;
};

const languageLookup = createChoiceLookup(pblLanguages, languageLabelZhMap, languageAliases);
const qualificationLookup = createChoiceLookup(
  pblProfessionalQualifications,
  qualificationLabelZhMap,
  qualificationAliases
);
const positionLookup = createChoiceLookup(pblLawFirmPositions, positionLabelZhMap, positionAliases);
const serviceAreaLookup = createChoiceLookup(pblServiceAreas, serviceAreaLabelZhMap, serviceAreaAliases);

const choiceItems = (values: unknown): unknown[] => {
  if (Array.isArray(values)) return values;
  if (typeof values === 'string') return values.split(/[,、]/);
  return [];
};

const normalizeChoiceValues = <Value extends string>(values: unknown, lookup: Map<string, Value>): Value[] =>
  choiceItems(values).reduce<Value[]>((normalizedValues, value) => {
    const normalizedValue = lookup.get(normalizeLookupKey(value));
    if (normalizedValue && !normalizedValues.includes(normalizedValue)) normalizedValues.push(normalizedValue);
    return normalizedValues;
  }, []);

export const normalizePblLanguageValues = (values: unknown): PblLanguageValue[] =>
  normalizeChoiceValues(values, languageLookup);

export const normalizePblProfessionalQualificationValues = (values: unknown): PblProfessionalQualificationValue[] =>
  normalizeChoiceValues(values, qualificationLookup);

export const normalizePblServiceAreaValues = (values: unknown): PblServiceArea[] =>
  normalizeChoiceValues(values, serviceAreaLookup);

export const normalizePblLawFirmPositionValue = (value: unknown): PblLawFirmPositionValue | '' =>
  positionLookup.get(normalizeLookupKey(value)) || '';

const inferOtherText = (displayValue: unknown, lookup: Map<string, string>) => {
  if (typeof displayValue !== 'string') return '';

  return displayValue
    .split(/[,、]/)
    .map((value) => value.trim())
    .filter((value) => value && !lookup.has(normalizeLookupKey(value)))
    .join(', ');
};

export const inferPblLanguageOtherText = (displayValue: unknown) => inferOtherText(displayValue, languageLookup);

export const inferPblProfessionalQualificationOtherText = (displayValue: unknown) =>
  inferOtherText(displayValue, qualificationLookup);

const getMapLabels = <Value extends string>(
  values: Value[],
  options: ReadonlyArray<ChoiceOption<Value>>,
  zhMap: Record<Value, string>
) => {
  const en = values.map((value) => options.find((item) => item.value === value)?.defaultLabel || value).join(', ');
  const zh = values.map((value) => zhMap[value] || value).join('、');
  return { en, zh };
};

export type PblCvChoiceSource = {
  position_in_law_firm?: string | null;
  position_other_text?: string | null;
  other_professional_qualifications?: string[];
  other_professional_qualification_text?: string | null;
  language_proficiency?: string[];
  language_other_text?: string | null;
  service_areas?: string[];
};

export const buildPblCvChoiceTextValues = (source: PblCvChoiceSource) => {
  const position = normalizePblLawFirmPositionValue(source.position_in_law_firm);
  const professionalQualifications = normalizePblProfessionalQualificationValues(
    source.other_professional_qualifications
  );
  const languages = normalizePblLanguageValues(source.language_proficiency);
  const serviceAreas = normalizePblServiceAreaValues(source.service_areas);
  const qualifications = getMapLabels(
    professionalQualifications,
    pblProfessionalQualifications,
    qualificationLabelZhMap
  );
  const languageLabels = getMapLabels(languages, pblLanguages, languageLabelZhMap);
  const values = {
    cv_position_en: pblLawFirmPositions.find((item) => item.value === position)?.defaultLabel || '',
    cv_position_zh: position ? positionLabelZhMap[position] : '',
    cv_other_qualifications_en: qualifications.en,
    cv_other_qualifications_zh: qualifications.zh,
    cv_languages_en: languageLabels.en,
    cv_languages_zh: languageLabels.zh,
    cv_primary_practice_areas_en: serviceAreas
      .map((value) => pblServiceAreas.find((item) => item.value === value)?.defaultLabel || value)
      .join(', '),
    cv_primary_practice_areas_zh: serviceAreas.map((value) => serviceAreaLabelZhMap[value] || value).join('、'),
  };

  if (position === 'other' && source.position_other_text) {
    values.cv_position_en = source.position_other_text;
    values.cv_position_zh = source.position_other_text;
  }

  if (professionalQualifications.includes('other') && source.other_professional_qualification_text) {
    const append = source.other_professional_qualification_text;
    values.cv_other_qualifications_en = values.cv_other_qualifications_en
      ? `${values.cv_other_qualifications_en}, ${append}`
      : append;
    values.cv_other_qualifications_zh = values.cv_other_qualifications_zh
      ? `${values.cv_other_qualifications_zh}、${append}`
      : append;
  }

  if (languages.includes('other') && source.language_other_text) {
    const append = source.language_other_text;
    values.cv_languages_en = values.cv_languages_en ? `${values.cv_languages_en}, ${append}` : append;
    values.cv_languages_zh = values.cv_languages_zh ? `${values.cv_languages_zh}、${append}` : append;
  }

  return values;
};
